import React, { useMemo, useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
  Form,
  Input,
  preventNegativeValues,
  preventPasteNegative,
} from "edviron-ui-package";
import Select from "react-select";
import { toast } from "react-toastify";
import { CREATE_SUBSCRIPTION } from "../../../Qurries";
import { CustomDropdownIndicator } from "../Settlement/Settlement";
import { useQuery } from "@apollo/client";
import { GET_PLANS, GET_USER } from "../../../Qurries";
import { PLAN_CURRENCY } from "../Plans/CreatePlan";

function localDateToSafeISOString(d: Date) {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day, 12, 0, 0)).toISOString();
}

function formatDateForInput(isoOrDate?: string | Date) {
  if (!isoOrDate) return "";
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateFromIsoOrString(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function toInputDateString(d?: Date) {
  if (!d) return "";
  return formatDateForInput(d);
}

function safeNumber(v?: string) {
  if (v === undefined || v === null || String(v).trim() === "") return NaN;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

const safeOptions = <T,>(o: T[] | undefined | null) =>
  Array.isArray(o) ? o : [];
const safeFind = <T extends { value?: any }>(
  list: T[] | undefined | null,
  predicate: (x: T) => boolean
) => (Array.isArray(list) ? list.find(predicate) : undefined) ?? null;

type SubscriptionType = "on_demand" | "periodic";

interface PlanDetails {
  planName: string;
  subscriptionType: SubscriptionType;
  currency: string;
  maximumAmount: string;
  immediateCharges: string;
  maxDebits: string;
  recurringAmount: string;
  chargeEvery: string;
  chargeInterval: string;
  firstChargeDate: string;
  immediateChargeDate?: string;
  subscriptionEnds: "on" | "until_cancelled";
  subscriptionEndsOnDate: string;
  enableTPV: boolean;
  returnUrl: string;
}

enum AccountType {
  Savings = "SAVINGS",
  Current = "CURRENT",
}

const initialPlanDetails: PlanDetails = {
  planName: "",
  subscriptionType: "on_demand",
  currency: "INR",
  maximumAmount: "",
  immediateCharges: "",
  maxDebits: "",
  recurringAmount: "",
  chargeEvery: "1",
  chargeInterval: "DAY",
  firstChargeDate: "",
  immediateChargeDate: "",
  subscriptionEnds: "until_cancelled",
  subscriptionEndsOnDate: "",
  enableTPV: false,
  returnUrl: "",
};

// const PLAN_OPTIONS = [
//   { label: "Create Subscription without Plan", value: "without_plan" },
//   { label: "Fees 2026 - Monthly", value: "fees_2026_monthly" },
//   { label: "Hostel Yearly", value: "hostel_yearly" },
// ];

// const PLAN_PRESETS: Record<string, Partial<PlanDetails>> = {
//   fees_2026_monthly: {
//     planName: "Fees 2026 - Monthly",
//     subscriptionType: "periodic",
//     currency: "INR",
//     maximumAmount: "50.00",
//     recurringAmount: "5.00",
//     chargeEvery: "1",
//     chargeInterval: "MONTH",
//     maxDebits: "5",
//   },
//   hostel_yearly: {
//     subscriptionType: "periodic",
//     currency: "INR",
//     maximumAmount: "600.00",
//     recurringAmount: "50.00",
//     chargeEvery: "12",
//     chargeInterval: "MONTH",
//     maxDebits: "4",
//   },
// };

const ACCOUNT_TYPE = [
  { label: "Savings", value: "SAVINGS" },
  { label: "Current", value: "CURRENT" },
];

const CHARGE_EVERY_OPTIONS = [
  { label: "Day(s)", value: "DAY" },
  { label: "Week(s)", value: "WEEK" },
  { label: "Month(s)", value: "MONTH" },
  { label: "Year(s)", value: "YEAR" },
];

const selectStyles = {
  control: (provided: any) => ({
    ...provided,

    backgroundColor: "#F6F8FA",
    border: "1px solid #1B163B",
    borderRadius: 4,
    minHeight: 20,
    boxShadow: "none",
  }),
  valueContainer: (provided: any) => ({ ...provided, padding: "2px 8px" }),
  input: (provided: any) => ({ ...provided, margin: 0, padding: 0 }),
  indicatorsContainer: (provided: any) => ({ ...provided, paddingRight: 8 }),
};

function ensureOption<T>(opts: T[], incoming: any): T | null {
  if (!incoming) return null;
  const val = Array.isArray(incoming) ? incoming[0] : incoming;
  if (typeof val === "string" || typeof val === "number") {
    return (opts as any).find((o: any) => o.value === val) ?? null;
  }
  if (val && (val.value !== undefined || val.label !== undefined)) {
    return (opts as any).find((o: any) => o.value === val.value) ?? null;
  }
  return null;
}

function sanitizeForForm(children: React.ReactNode[]) {
  return React.Children.toArray(children).map((child, idx) => {
    if (!React.isValidElement(child)) return child;
    const propsToAdd: any = {};
    if ((child as any).props && (child as any).props.add_error === undefined) {
      propsToAdd.add_error = () => {};
    }
    if ((child as any).props && (child as any).props.name === undefined) {
      propsToAdd.name = `field_${idx}`;
    }
    return React.cloneElement(child as any, propsToAdd);
  });
}

export interface CreateSubscriptionFormProps {
  setOpen: (open: boolean) => void;
  onCreated?: () => void;
}

function CreateSubscriptionForm({
  setOpen,
  onCreated,
}: CreateSubscriptionFormProps) {
  const [step, setStep] = useState<1 | 2>(1);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [accountNumber, setAccountNumber] = useState<string>("");
  const [accountType, setAccountType] = useState<string>(AccountType.Savings);

  const [bankName, setBankName] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [notifyCustomer, setNotifyCustomer] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useState<string>("without_plan");

  const [planDetails, setPlanDetails] =
    useState<PlanDetails>(initialPlanDetails);
  const [submitting, setSubmitting] = useState(false);

  const {
    data: user_data
  } = useQuery(GET_USER);

  const selectedSchoolId = user_data?.getMerchantQuery?.school_id;

  const [createSubscription] = useMutation(CREATE_SUBSCRIPTION);

  const { data: plansRes } = useQuery(GET_PLANS, {
    variables: {
      filter: {
        page: 1,
        limit: 100,
      },
    },
    skip: !selectedSchoolId,
  });

  const isUsingExistingPlan = selectedPlanId !== "without_plan";



  type PlanOption = {
    label: string;
    value: string;
    raw?: any;
  };

  const planOptions: PlanOption[] = useMemo(() => {
    const plans = plansRes?.getPlansMerchant?.data ?? [];

    return [
      { label: "Create Subscription without Plan", value: "without_plan" },
      ...plans.map((p: any, index: number) => ({
        label: p.plan_name,
        value: `${p.plan_name ?? "plan"}__${p.createdAt ?? ""}__${index}`,
        raw: p,
      })),
    ];
  }, [plansRes]);

  const onSelectPlan = (opt: PlanOption | null) => {
    const value = opt?.value ?? "without_plan";
    setSelectedPlanId(value);

    if (value === "without_plan") {
      setPlanDetails(initialPlanDetails);
      return;
    }

    const p = opt!.raw;

    setPlanDetails({
      planName: p.plan_name ?? "",
      subscriptionType: p.plan_type === "PERIODIC" ? "periodic" : "on_demand",
      currency: p.currency ?? "INR",
      maximumAmount: String(p.plan_max_amount ?? ""),
      recurringAmount: String(p.plan_recurring_amount ?? ""),
      maxDebits: String(p.plan_max_cycles ?? ""),
      chargeEvery: String(p.plan_intervals ?? "1"),
      chargeInterval: p.plan_interval_type ?? "MONTH",
      immediateCharges: "",
      firstChargeDate: "",
      subscriptionEnds: "until_cancelled",
      subscriptionEndsOnDate: "",
      enableTPV: false,
      returnUrl: "",
    });
  };

  const validateStep1 = () => {
    const n = name.trim();
    const e = email.trim();
    const m = mobile.trim();
    const r = subscriptionId.trim();

    const accNo = accountNumber.trim();
    const accHolder = accountHolderName.trim();
    const ifsc = ifscCode.trim();
    const bank = bankName.trim();
    const accType = accountType?.trim();

    if (!n) {
      toast.error("Name is required");
      return false;
    }
    if (!e) {
      toast.error("Email is required");
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(e)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    
    if (m.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return false;
    }
    
    if (!r) {
      toast.error("Subscription ID is required");
      return false;
    }
    
    if (!accNo) {
      toast.error("Account Number is required");
      return false;
    }
    if (accNo.length < 6) {
      toast.error("Account Number looks invalid");
      return false;
    }
    
    if (!accHolder) {
      toast.error("Account Holder Name is required");
      return false;
    }
    
    if (!accType) {
      toast.error("Account Type is required");
      return false;
    }
    
    if (!bank) {
      toast.error("Bank Code Name is required");
      return false;
    }
    
    if (!ifsc) {
      toast.error("IFSC Code is required");
      return false;
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase())) {
      toast.error("Invalid IFSC Code format");
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    const {
      planName,
      subscriptionType,
      currency,
      maximumAmount,
      immediateCharges,
      maxDebits,
      recurringAmount,
      firstChargeDate,
      subscriptionEnds,
      subscriptionEndsOnDate,
      returnUrl,
    } = planDetails;

    if (!selectedPlanId) {
      toast.error("Please select a plan");
      return false;
    }

    if (!planName.trim()) {
      toast.error("Plan Name is required");
      return false;
    }
    
    if (!currency) {
      toast.error("Subscription Currency is required");
      return false;
    }

    const maxAmt = Number(maximumAmount);
    if (Number.isNaN(maxAmt) || maxAmt <= 0) {
      toast.error("Maximum Amount must be greater than 0");
      return false;
    }

    if (immediateCharges) {
      const imm = Number(immediateCharges);
      if (Number.isNaN(imm) || imm < 0) {
        toast.error("Immediate Charges cannot be negative");
        return false;
      }
    }

    if (maxDebits) {
      const debits = Number(maxDebits);
      if (!Number.isInteger(debits) || debits <= 0) {
        toast.error("Maximum Number of Debits must be a positive number");
        return false;
      }
    }

    if (subscriptionType === "periodic") {
      const recur = Number(recurringAmount);
      if (Number.isNaN(recur) || recur <= 0) {
        toast.error("Recurring Amount is required for Periodic plans");
        return false;
      }

      if (!firstChargeDate) {
        toast.error("First Charge Date is required");
        return false;
      }
    }

    if (subscriptionEnds === "on" && !subscriptionEndsOnDate) {
      toast.error("Subscription End Date is required");
      return false;
    }

    if (returnUrl && !/^https?:\/\/.+/.test(returnUrl)) {
      toast.error("Return URL must be a valid URL");
      return false;
    }

    return true;
  };

  const handlePlanFieldChange = (
    field: keyof PlanDetails | keyof typeof initialPlanDetails,
    value: string | boolean
  ) => {
    const dateFields = ["firstChargeDate", "immediateChargeDate"];
    if (isUsingExistingPlan && !dateFields.includes(String(field))) return;

    setPlanDetails((p) => ({ ...p, [field]: value } as PlanDetails));
  };

  useEffect(() => {
    setSelectedPlanId("without_plan");
  }, [selectedSchoolId]);

  const handleDateChange = (field: keyof PlanDetails, yyyyMmDd: string) => {
    if (!yyyyMmDd) {
      handlePlanFieldChange(field, "");
      return;
    }
    const [y, m, d] = yyyyMmDd.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    handlePlanFieldChange(field, localDateToSafeISOString(date));
  };

  const buildSubscriptionPayload = () => {
    const maxAmountNum = planDetails.maximumAmount
      ? Number(planDetails.maximumAmount)
      : null;
    const recurringAmountNum = planDetails.recurringAmount
      ? Number(planDetails.recurringAmount)
      : null;
    const immediateChargesNum = planDetails.immediateCharges
      ? Number(planDetails.immediateCharges)
      : 0;
    const maxCyclesNum = planDetails.maxDebits
      ? Number(planDetails.maxDebits)
      : null;
    const intervalsNum = planDetails.chargeEvery
      ? Number(planDetails.chargeEvery)
      : null;

    const isPeriodic = planDetails.subscriptionType === "periodic";

    const firstChargeTime =
      isPeriodic && planDetails.firstChargeDate
        ? planDetails.firstChargeDate
        : undefined;

    const plan_details: any = {
      plan_id: "",
      plan_name: planDetails.planName || undefined,

      plan_type: isPeriodic ? "PERIODIC" : "ON_DEMAND",
      ...(isPeriodic ? { plan_amount: recurringAmountNum ?? undefined } : {}),
      ...(maxAmountNum != null ? { plan_max_amount: maxAmountNum } : {}),
      ...(maxCyclesNum != null ? { plan_max_cycles: maxCyclesNum } : {}),
      ...(intervalsNum != null && isPeriodic
        ? { plan_intervals: intervalsNum }
        : {}),
      ...(isPeriodic
        ? { plan_interval_type: planDetails.chargeInterval || "MONTH" }
        : {}),
      plan_currency: planDetails.currency || "INR",
      plan_note: planDetails.planName || undefined,
    };

    const authorization_details: any = {
      authorization_amount: isPeriodic
        ? immediateChargesNum > 0
          ? immediateChargesNum
          : 1
        : 1,
      authorization_amount_refund: true,
    };

    const payload: any = {
      subscription_id: subscriptionId || undefined,
      school_id: selectedSchoolId || undefined,
      ...(firstChargeTime
        ? { subscription_first_charge_time: firstChargeTime }
        : {}),
      customer_details: {
        customer_name: name || undefined,
        customer_email: email || undefined,
        customer_phone: mobile || undefined,
        customer_bank_account_number: accountNumber || undefined,
        customer_bank_ifsc: ifscCode || undefined,
        customer_bank_code: bankName || undefined,
        customer_bank_account_type: accountType || undefined,
        customer_bank_account_holder_name: accountHolderName || undefined,
      },
      plan_details,
      authorization_details,
      subscription_meta: {
        return_url: planDetails.returnUrl || undefined,
        notification_channel: notifyCustomer ? ["EMAIL"] : undefined,
      },
    };

    function prune(obj: any, seen = new WeakSet()): any {
      if (obj === null || obj === undefined) return undefined;

      if (typeof obj !== "object" || obj instanceof Date) {
        return obj;
      }

      if (seen.has(obj)) {
        return undefined;
      }
      seen.add(obj);

      if (Array.isArray(obj)) {
        const out: any[] = [];
        for (const it of obj) {
          const p = prune(it, seen);
          if (p === undefined) continue;
          out.push(p);
        }
        return out.length ? out : undefined;
      }

      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (v === undefined || v === null) continue;
        if (typeof v === "string") {
          if (v.trim() === "") continue;
          out[k] = v;
          continue;
        }
        if (typeof v === "function") continue;
        if (v instanceof Date) {
          out[k] = v;
          continue;
        }
        if (typeof v === "object") {
          const p = prune(v, seen);
          if (p === undefined) continue;
          out[k] = p;
          continue;
        }
        out[k] = v;
      }
      return Object.keys(out).length ? out : undefined;
    }

    const cleaned = prune(payload);
    return { subscription: cleaned };
  };

  const validateDatesBeforeSubmit = () => {
    if (planDetails.firstChargeDate) {
      const fc = dateFromIsoOrString(planDetails.firstChargeDate);
      if (!fc) {
        toast.error("First charge date is invalid.");
        return false;
      }
      const minAllowed = addDays(new Date(), 2);
      const fcDateOnly = new Date(
        fc.getFullYear(),
        fc.getMonth(),
        fc.getDate()
      );
      const minDateOnly = new Date(
        minAllowed.getFullYear(),
        minAllowed.getMonth(),
        minAllowed.getDate()
      );
      if (fcDateOnly < minDateOnly) {
        toast.error("First Charge Date must be at least 2 days from today.");
        return false;
      }
    }

    if (
      planDetails.subscriptionEnds === "on" &&
      planDetails.subscriptionEndsOnDate
    ) {
      const end = dateFromIsoOrString(planDetails.subscriptionEndsOnDate);
      if (!end) {
        toast.error("Subscription end date is invalid.");
        return false;
      }
      const endDateOnly = new Date(
        end.getFullYear(),
        end.getMonth(),
        end.getDate()
      );

      if (planDetails.firstChargeDate) {
        const fc = dateFromIsoOrString(planDetails.firstChargeDate);
        if (fc) {
          const fcDateOnly = new Date(
            fc.getFullYear(),
            fc.getMonth(),
            fc.getDate()
          );
          if (endDateOnly < fcDateOnly) {
            toast.error(
              "Subscription end date cannot be before First Charge Date."
            );
            return false;
          }
        }
      } else {
        const today = new Date();
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        if (endDateOnly < todayOnly) {
          toast.error("Subscription end date cannot be in the past.");
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const maxAmount = safeNumber(planDetails.maximumAmount);
    const recurring = safeNumber(planDetails.recurringAmount);
    // const immediate = safeNumber(planDetails.immediateCharges);

    const isOnDemand = planDetails.subscriptionType === "on_demand";
    const isPeriodic = planDetails.subscriptionType === "periodic";

    if (isOnDemand) {
      if (Number.isNaN(maxAmount) || maxAmount <= 0) {
        toast.error(
          "For On-Demand subscriptions you must set a positive Maximum Amount."
        );
        return;
      }
    }

    if (
      planDetails.subscriptionType === "periodic" &&
      !planDetails.firstChargeDate
    ) {
      toast.error("First Charge Date is required for Periodic subscriptions.");
      return;
    }

    if (isPeriodic) {
      if (Number.isNaN(recurring) || recurring <= 0) {
        toast.error(
          "For Periodic subscriptions you must set a positive Recurring Amount."
        );
        return;
      }
    }

    if (!Number.isNaN(recurring) && !Number.isNaN(maxAmount)) {
      if (recurring > maxAmount) {
        toast.error("Recurring Amount cannot be greater than Maximum Amount.");
        return;
      }
    }

    const datesOk = validateDatesBeforeSubmit();
    if (!datesOk) return;

    try {
      setSubmitting(true);

      const vars = buildSubscriptionPayload();

      await createSubscription({
        variables: {
          subscription: vars.subscription,
        },
      });

      toast.success("Subscription created successfully");
      onCreated?.();
      setOpen(false);
    } catch (e: any) {
      toast.error(
        e?.graphQLErrors?.[0]?.message || "Failed to create subscription"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inputProps = (nameProp: string, required: boolean) => ({
    name: (
      <span className="flex items-center gap-1">
        {nameProp}
        {required && <span className="text-red-500">*</span>}
      </span>
    ),
    add_error: () => {},
  });

  const firstChargeMinDate = toInputDateString(addDays(new Date(), 2));
  const todayInputDate = toInputDateString(new Date());

  const subscriptionEndsMinDate = planDetails.firstChargeDate
    ? formatDateForInput(planDetails.firstChargeDate)
    : todayInputDate;

  return (
    <div className="flex flex-col bg-white rounded-[4px] overflow-y-auto h-[80vh] w-full">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
        <div className="flex items-center gap-2">
          {step === 2 && (
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-[11px] font-semibold text-[#6667FF] flex items-center gap-1"
            >
              <span>{"<"}</span> Back
            </button>
          )}
          <p className="font-medium">
            {step === 1 ? "Customer Details" : "Subscription Details"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] bg-[#9CA3AF] bg-opacity-80 text-white px-2 py-[2px] rounded-[2px]">
            Step {step}/2
          </span>
        </div>
      </div>

      <div className="flex-1 px-6 py-4">
        {step === 1 ? (
          <Form onSubmit={() => setStep(2)}>
            {sanitizeForForm([
              <>
                <div className="w-full grid grid-cols-2 gap-6">
                  <Input
                    {...inputProps("Name", true)}
                    type="name"
                    placeholder="Enter Name"
                    value={name}
                    onChange={(a: any, b?: any) =>
                      setName(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                  <Input
                    {...inputProps("Email", true)}
                    type="email"
                    placeholder="Enter Customer Email"
                    value={email}
                    onChange={(a: any, b?: any) =>
                      setEmail(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                </div>

                <div className="w-full grid grid-cols-2 gap-6 mt-4">
                  <Input
                    {...inputProps("Mobile Number", true)}
                    type="number"
                    placeholder="Enter Mobile Number"
                    value={mobile}
                    maxLength={10}
                    onKeyDown={(e: any) => {
                      preventNegativeValues(e);
                    }}
                    onPaste={preventPasteNegative}
                    onChange={(a: any, b?: any) => {
                      const raw =
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b;
                      const digits = String(raw)
                        .replace(/[^0-9]/g, "")
                        .slice(0, 10);
                      setMobile(digits);
                    }}
                  />
                  <Input
                    {...inputProps("Subscription ID", true)}
                    type="name"
                    placeholder="Enter Subscription ID"
                    value={subscriptionId}
                    onChange={(a: any, b?: any) =>
                      setSubscriptionId(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                </div>

                <div className="w-full grid grid-cols-2 gap-6 mt-4">
                  <Input
                    {...inputProps("Account Number", true)}
                    type="number"
                    placeholder="Enter Account Number"
                    value={accountNumber}
                    onChange={(a: any, b?: any) =>
                      setAccountNumber(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                  <Input
                    {...inputProps("IFSC Code", true)}
                    type="name"
                    placeholder="Enter IFSC Code"
                    value={ifscCode}
                    onChange={(a: any, b?: any) =>
                      setIfscCode(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-6 mt-4">
                  <Input
                    {...inputProps("Account Holder Name", true)}
                    type="name"
                    placeholder="Enter Account Holder Name"
                    value={accountHolderName}
                    onChange={(a: any, b?: any) =>
                      setAccountHolderName(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                  <div>
                    <p className="text-sm leading-6 font-medium text-gray-900 my-2">
                      Account Type
                      <span className="text-red-500 pl-1">*</span>
                    </p>

                    <Select
                      options={safeOptions(ACCOUNT_TYPE)}
                      value={safeFind(
                        ACCOUNT_TYPE,
                        (c) => c.value === accountType
                      )}
                      onChange={(v: any) => {
                        const resolved = ensureOption(ACCOUNT_TYPE, v);
                        if (resolved) setAccountType(String(resolved.value));
                      }}
                      components={{
                        DropdownIndicator: CustomDropdownIndicator,
                        IndicatorSeparator: () => null,
                      }}
                      styles={selectStyles}
                      isDisabled={false}
                    />
                  </div>
                </div>

                <div className="w-full grid grid-cols-2 gap-6 mt-4">
                  <Input
                    {...inputProps("Bank Code Name", true)}
                    type="name"
                    placeholder="Enter Code Name for ex: HDFC"
                    value={bankName}
                    onChange={(a: any, b?: any) =>
                      setBankName(
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />
                </div>

                <div className="mt-4">
                  <label className="flex items-center gap-2 text-xs text-[#1B163B]">
                    <input
                      type="checkbox"
                      checked={notifyCustomer}
                      onChange={(e) => setNotifyCustomer(e.target.checked)}
                      className="h-3 w-3 rounded border-gray-300 pr-2"
                    />
                    <span>Send Email notifications to the customer.</span>
                  </label>
                </div>
              </>,
            ])}
          </Form>
        ) : (
          <Form onSubmit={handleSubmit}>
            {sanitizeForForm([
              <>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-900 mb-1 block">
                      Select Plan
                      <span className="text-red-500 pl-1">*</span>
                    </label>
                    <Select
                      options={planOptions}
                      value={
                        planOptions.find((p) => p.value === selectedPlanId) ||
                        null
                      }
                      onChange={(opt: any) => {
                        onSelectPlan(opt);
                      }}
                      isMulti={false}
                      closeMenuOnSelect
                      blurInputOnSelect
                      getOptionLabel={(opt) => opt.label}
                      getOptionValue={(opt) => opt.value}
                      components={{
                        DropdownIndicator: CustomDropdownIndicator,
                        IndicatorSeparator: () => null,
                      }}
                      styles={selectStyles}
                    />
                  </div>

                  <div className="py-2 border border-gray-300 rounded-[4px] overflow-hidden px-3 flex items-center justify-between">
                    <p className="text-sm leading-6 font-medium text-gray-900 ">
                      Subscription Type
                      <span className="text-red-500 pl-1">*</span>
                    </p>
                    <div className="flex gap-6 text-xs text-[#1B163B]">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          checked={planDetails.subscriptionType === "on_demand"}
                          disabled={isUsingExistingPlan}
                          onChange={() =>
                            handlePlanFieldChange(
                              "subscriptionType",
                              "on_demand"
                            )
                          }
                        />{" "}
                        On-Demand
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          checked={planDetails.subscriptionType === "periodic"}
                          disabled={isUsingExistingPlan}
                          onChange={() =>
                            handlePlanFieldChange(
                              "subscriptionType",
                              "periodic"
                            )
                          }
                        />{" "}
                        Periodic
                      </label>
                    </div>
                  </div>

                  <Input
                    {...inputProps("Plan Name", true)}
                    placeholder="Plan Name"
                    value={planDetails.planName}
                    disabled={isUsingExistingPlan}
                    onChange={(a: any, b?: any) =>
                      handlePlanFieldChange(
                        "planName",
                        b === undefined
                          ? typeof a === "string"
                            ? a
                            : a?.target?.value ?? ""
                          : b
                      )
                    }
                  />

                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex justify-center flex-col">
                      <p className="text-sm leading-6 font-medium text-gray-900 mb-2">
                        Subscription Currency
                        <span className="text-red-500 pl-1">*</span>
                      </p>
                      <Select
                        options={safeOptions(PLAN_CURRENCY)}
                        value={safeFind(
                          PLAN_CURRENCY,
                          (c) => c.value === planDetails.currency
                        )}
                        isDisabled={isUsingExistingPlan}
                        onChange={(v: any) => {
                          const resolved = ensureOption(PLAN_CURRENCY, v);
                          if (resolved)
                            handlePlanFieldChange(
                              "currency",
                              String(resolved.value)
                            );
                        }}
                        components={{
                          DropdownIndicator: CustomDropdownIndicator,
                          IndicatorSeparator: () => null,
                        }}
                        styles={selectStyles}
                      />
                    </div>

                    <div className="w-full">
                      <Input
                        {...inputProps(`Maximum Amount`, true)}
                        type="number"
                        value={planDetails.maximumAmount}
                        disabled={isUsingExistingPlan}
                        min={0}
                        onChange={(a: any, b?: any) =>
                          handlePlanFieldChange(
                            "maximumAmount",
                            b === undefined
                              ? typeof a === "string"
                                ? a
                                : a?.target?.value ?? ""
                              : b
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6 mt-4">
                    <div>
                      <Input
                        {...inputProps("Immediate Charges", false)}
                        type="number"
                        value={planDetails.immediateCharges}
                        disabled={isUsingExistingPlan}
                        min={0}
                        onChange={(a: any, b?: any) =>
                          handlePlanFieldChange(
                            "immediateCharges",
                            b === undefined
                              ? typeof a === "string"
                                ? a
                                : a?.target?.value ?? ""
                              : b
                          )
                        }
                      />
                    </div>

                    <div>
                      <Input
                        {...inputProps("Maximum Number Of Debits", false)}
                        type="number"
                        value={planDetails.maxDebits}
                        disabled={isUsingExistingPlan}
                        min={0}
                        onChange={(a: any, b?: any) =>
                          handlePlanFieldChange(
                            "maxDebits",
                            b === undefined
                              ? typeof a === "string"
                                ? a
                                : a?.target?.value ?? ""
                              : b
                          )
                        }
                      />
                    </div>
                  </div>

                  {planDetails.subscriptionType === "periodic" ? (
                    <>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <Input
                            {...inputProps("Recurring Amount", true)}
                            type="number"
                            value={planDetails.recurringAmount}
                            disabled={isUsingExistingPlan}
                            min={0}
                            onChange={(a: any, b?: any) =>
                              handlePlanFieldChange(
                                "recurringAmount",
                                b === undefined
                                  ? typeof a === "string"
                                    ? a
                                    : a?.target?.value ?? ""
                                  : b
                              )
                            }
                          />
                        </div>

                        <div className="flex justify-center flex-col">
                          <p className="text-sm leading-6 font-medium text-gray-900 mb-1">
                            Charge Customer Once Every
                            <span className="text-red-500 pl-1">*</span>
                          </p>
                          <div className="flex gap-2 items-center">
                            <div className="flex items-center border rounded-[4px] p-[6px]">
                              <button
                                type="button"
                                className="px-1"
                                onClick={() => {
                                  const cur = Number(planDetails.chargeEvery);
                                  const safeCur =
                                    Number.isFinite(cur) && cur > 0 ? cur : 1;
                                  const next = Math.max(1, safeCur - 1);
                                  handlePlanFieldChange(
                                    "chargeEvery",
                                    String(next)
                                  );
                                }}
                                disabled={
                                  Number(planDetails.chargeEvery) <= 1 ||
                                  !Number.isFinite(
                                    Number(planDetails.chargeEvery)
                                  )
                                }
                                aria-label="Decrease charge interval"
                              >
                                -
                              </button>

                              <div className="px-3 select-none">
                                {Number.isFinite(
                                  Number(planDetails.chargeEvery)
                                )
                                  ? planDetails.chargeEvery
                                  : "1"}
                              </div>

                              <button
                                type="button"
                                className="px-1"
                                onClick={() => {
                                  const cur = Number(planDetails.chargeEvery);
                                  const safeCur =
                                    Number.isFinite(cur) && cur > 0 ? cur : 1;
                                  const MAX = 999;
                                  const next = Math.min(MAX, safeCur + 1);
                                  handlePlanFieldChange(
                                    "chargeEvery",
                                    String(next)
                                  );
                                }}
                                aria-label="Increase charge interval"
                              >
                                +
                              </button>
                            </div>

                            <Select
                              options={safeOptions(CHARGE_EVERY_OPTIONS)}
                              value={safeFind(
                                CHARGE_EVERY_OPTIONS,
                                (c) => c.value === planDetails.chargeInterval
                              )}
                              isDisabled={isUsingExistingPlan}
                              onChange={(v: any) => {
                                const resolved = ensureOption(
                                  CHARGE_EVERY_OPTIONS,
                                  v
                                );
                                if (resolved)
                                  handlePlanFieldChange(
                                    "chargeInterval",
                                    String(resolved.value)
                                  );
                              }}
                              styles={selectStyles}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="py-2 rounded-[4px] overflow-hidden w-full flex flex-col">
                          <p className="text-sm leading-6 font-medium text-gray-900 pb-2 ">
                            First Charge Date
                            <span className="text-red-500 pl-1">*</span>
                          </p>
                          <input
                            type="date"
                            className="px-3 py-2 border rounded-md text-sm"
                            value={formatDateForInput(
                              planDetails.firstChargeDate
                            )}
                            min={firstChargeMinDate}
                            onChange={(e) =>
                              handleDateChange(
                                "firstChargeDate",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* on_demand branch — immediate charges and max debits already rendered above */}
                    </>
                  )}

                  <div className=" w-full pb-2">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="flex justify-center flex-col">
                        <p className="text-sm leading-6 font-medium text-gray-900">
                          Subscription Ends
                          <span className="text-red-500 pl-1">*</span>
                        </p>
                        <div className="flex gap-6 items-center pt-2">
                          <label className="flex items-center gap-2 text-sm text-gray-900">
                            <input
                              type="radio"
                              checked={planDetails.subscriptionEnds === "on"}
                              disabled={isUsingExistingPlan}
                              onChange={() =>
                                handlePlanFieldChange("subscriptionEnds", "on")
                              }
                            />
                            <span>On</span>
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-900">
                            <input
                              type="radio"
                              checked={
                                planDetails.subscriptionEnds ===
                                "until_cancelled"
                              }
                              disabled={isUsingExistingPlan}
                              onChange={() =>
                                handlePlanFieldChange(
                                  "subscriptionEnds",
                                  "until_cancelled"
                                )
                              }
                            />
                            <span>Until Cancelled</span>
                          </label>
                        </div>
                      </div>

                      {planDetails.subscriptionEnds === "on" && (
                        <div className="">
                          <label className="text-sm font-medium leading-6 text-gray-900">
                            Subscription Ends On
                            <span className="text-red-500 pl-1">*</span>
                          </label>
                          <div className="pt-2">
                            <input
                              type="date"
                              className="w-full px-3 py-2 border rounded-md text-sm"
                              value={formatDateForInput(
                                planDetails.subscriptionEndsOnDate
                              )}
                              min={subscriptionEndsMinDate}
                              onChange={(e) =>
                                handleDateChange(
                                  "subscriptionEndsOnDate",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <Input
                      {...inputProps("Return URL", false)}
                      placeholder="https://..."
                      value={planDetails.returnUrl}
                      disabled={isUsingExistingPlan}
                      onChange={(a: any, b?: any) =>
                        handlePlanFieldChange(
                          "returnUrl",
                          b === undefined
                            ? typeof a === "string"
                              ? a
                              : a?.target?.value ?? ""
                            : b
                        )
                      }
                    />
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium leading-6 text-gray-900">
                          Enable Third Party Validation
                        </p>
                        <div className="flex ">
                          <p className="text-xs text-gray-500 mt-1">
                            TPV is supported only for bank accounts and UPI Auto
                            pay.
                          </p>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={planDetails.enableTPV}
                              disabled={isUsingExistingPlan}
                              onChange={(e) =>
                                handlePlanFieldChange(
                                  "enableTPV",
                                  e.target.checked
                                )
                              }
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>,
            ])}
          </Form>
        )}
      </div>

      <div className="px-6 py-4  border-t border-gray-200 flex justify-between items-center bg-white">
        <div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[13px] text-[#1E1B59]"
          >
            Cancel
          </button>
        </div>

        <div>
          {step === 1 ? (
            <button
              onClick={() => {
                if (!validateStep1()) return;
                setStep(2);
              }}
              className="bg-[#1E1B59] px-6 py-2 rounded-[4px] text-[13px] text-white"
            >
              Next
            </button>
          ) : (
            <button
              onClick={() => {
                if (!validateStep2()) return;
                handleSubmit();
              }}
              disabled={submitting}
              className="px-6 py-2 text-[13px] rounded-[4px] bg-edviron_black text-white disabled:bg-gray-300 flex items-center justify-center gap-2 min-w-[9rem]"
            >
              {submitting && (
                <AiOutlineLoading3Quarters className="text-xs animate-spin" />
              )}
              <span>Create Subscription</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateSubscriptionForm;
